/**
 * @module monetization/api
 *
 * Hand-written React Query hooks for the v1.3 monetization endpoints. These
 * are NOT part of the regenerated Orval client, so they follow the same shape
 * the generated client uses by hand: a `use*Hook` that wraps the request in a
 * `useCallback`-stable function via {@link useCustomInstance} (so the player
 * JWT is forwarded), a `get*QueryKey` helper, and a public hook that wraps
 * `useQuery` / `useMutation`.
 *
 * @see src/lib/use-custom-instance.ts — the auth-forwarding axios mutator.
 * @see src/lib/endpoints.ts — the generated client this mirrors.
 */
import { useCallback } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCustomInstance } from '@/lib/use-custom-instance';
import type { CashBackResponse, ExperienceActionDto, ExperienceImpressionDto, SurfaceItem } from './types';

/* ── Cash-Back balance ─────────────────────────────────────────────── */

export const getCashBackQueryKey = () => ['/me/cash-back'] as const;

const useCashBackHook = () => {
  const request = useCustomInstance<CashBackResponse>();
  return useCallback((signal?: AbortSignal) => request({ url: '/me/cash-back', method: 'GET', signal }), [request]);
};

/**
 * The player's Cash-Back (Crown Dollars) token balance + lots. Drives the
 * lobby balance pill.
 */
export function useCashBackBalance<TData = CashBackResponse, TError = unknown>(options?: {
  query?: Partial<UseQueryOptions<CashBackResponse, TError, TData>>;
}): UseQueryResult<TData, TError> {
  const cashBack = useCashBackHook();
  const { query } = options ?? {};
  return useQuery({
    queryKey: getCashBackQueryKey(),
    queryFn: ({ signal }) => cashBack(signal),
    ...query,
  });
}

/* ── Surfaces (lobby bar) ──────────────────────────────────────────── */

export const getSurfacesQueryKey = () => ['/monetization/surfaces'] as const;

const useSurfacesHook = () => {
  const request = useCustomInstance<SurfaceItem[]>();
  return useCallback((signal?: AbortSignal) => request({ url: '/monetization/surfaces', method: 'GET', signal }), [request]);
};

/** All resolved, server-ordered monetization surfaces for the current viewer. */
export function useSurfaces<TData = SurfaceItem[], TError = unknown>(options?: {
  query?: Partial<UseQueryOptions<SurfaceItem[], TError, TData>>;
}): UseQueryResult<TData, TError> {
  const surfaces = useSurfacesHook();
  const { query } = options ?? {};
  return useQuery({
    queryKey: getSurfacesQueryKey(),
    queryFn: ({ signal }) => surfaces(signal),
    ...query,
  });
}

/* ── Deposit offers (deposit screen) ───────────────────────────────── */

export const getDepositOffersQueryKey = () => ['/monetization/surfaces/deposit-offers'] as const;

const useDepositOffersHook = () => {
  const request = useCustomInstance<SurfaceItem[]>();
  return useCallback(
    (signal?: AbortSignal) => request({ url: '/monetization/surfaces/deposit-offers', method: 'GET', signal }),
    [request],
  );
};

/** The `OFFER` surfaces eligible to bind to a deposit. */
export function useDepositOffers<TData = SurfaceItem[], TError = unknown>(options?: {
  query?: Partial<UseQueryOptions<SurfaceItem[], TError, TData>>;
}): UseQueryResult<TData, TError> {
  const offers = useDepositOffersHook();
  const { query } = options ?? {};
  return useQuery({
    queryKey: getDepositOffersQueryKey(),
    queryFn: ({ signal }) => offers(signal),
    ...query,
  });
}

/* ── Claim a gift / record an impression ───────────────────────────── */

interface ClaimGiftVariables {
  experienceId: string;
  data: ExperienceActionDto;
}

const useClaimGiftHook = () => {
  const request = useCustomInstance<void>();
  return useCallback(
    ({ experienceId, data }: ClaimGiftVariables) =>
      request({
        url: `/monetization/experiences/${experienceId}/actions`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data,
      }),
    [request],
  );
};

/** Claim a gift surface (`actionType: 'claim'`). Mints the Cash-Back lot server-side. */
export function useClaimGift<TError = unknown, TContext = unknown>(options?: {
  mutation?: UseMutationOptions<void, TError, ClaimGiftVariables, TContext>;
}): UseMutationResult<void, TError, ClaimGiftVariables, TContext> {
  const claimGift = useClaimGiftHook();
  const queryClient = useQueryClient();
  const { mutation } = options ?? {};
  return useMutation({
    mutationKey: ['monetizationClaimGift'],
    mutationFn: (vars: ClaimGiftVariables) => claimGift(vars),
    ...mutation,
    // A successful claim mints tokens + drops the gift from the surface server-side,
    // so refresh both the bar and the balance pill.
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: getSurfacesQueryKey() });
      void queryClient.invalidateQueries({ queryKey: getCashBackQueryKey() });
      mutation?.onSuccess?.(...args);
    },
  });
}

interface ImpressionVariables {
  experienceId: string;
  data: ExperienceImpressionDto;
}

const useRecordImpressionHook = () => {
  const request = useCustomInstance<void>();
  return useCallback(
    ({ experienceId, data }: ImpressionVariables) =>
      request({
        url: `/monetization/experiences/${experienceId}/impressions`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data,
      }),
    [request],
  );
};

/**
 * Imperative impression helper. Returns a stable `record(experienceId)`
 * callback; pair with {@link useTrackImpression} to fire once when a surface
 * scrolls into view. Failures are swallowed — an impression must never crash
 * the host surface.
 */
export function useRecordImpression(): (experienceId: string) => void {
  const recordImpression = useRecordImpressionHook();
  return useCallback(
    (experienceId: string) => {
      void recordImpression({ experienceId, data: { clientEventId: newClientEventId() } }).catch(() => {
        /* impressions are best-effort */
      });
    },
    [recordImpression],
  );
}

/** A client-generated idempotency key for an experience action / impression. */
export function newClientEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `cev_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}
